import { Link, useLocation } from 'react-router-dom';

/**
 * What an unmatched URL renders.
 *
 * There was no `path="*"` at all. `/` is a layout route with children and no
 * catch-all, so a typo'd or retired URL matched NOTHING and React Router
 * rendered null — a blank white page with no nav, no footer and no way out
 * except the browser's back button. A dead end that looks like a crash is worse
 * than a dead end that says so.
 *
 * It sits INSIDE the layout route deliberately: the nav is the way out, so the
 * one screen whose whole job is "you are lost" is the last one that should drop
 * the chrome.
 */

/** The stages, in nav order. Kept local rather than imported from Layout's
 *  SECTIONS — that list is about highlighting the current route and carries
 *  sticky/extraPaths baggage this screen has no use for. */
const WAYS_OUT: { path: string; label: string; blurb: string }[] = [
  { path: '/', label: 'Home', blurb: 'What needs you today' },
  { path: '/subjects', label: 'Subjects', blurb: 'What you cover, and what you know about it' },
  { path: '/boardroom', label: 'Recon', blurb: 'Map a market with Recon' },
  { path: '/news-beats', label: 'News Beats', blurb: 'A standing watch on a topic' },
  { path: '/artifacts', label: 'Artifacts', blurb: 'Everything you have produced' },
  { path: '/deliver', label: 'Deliver', blurb: 'Sites, deploys and review' },
];

export default function NotFound() {
  const location = useLocation();

  return (
    <div className="max-w-2xl">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
        There is nothing at this address
      </h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
        {/* The path is worth showing: most arrivals here are a stale bookmark or
            a hand-edited URL, and seeing WHICH one is how you spot the typo. */}
        Nothing in the app answers to{' '}
        <code className="font-mono text-gray-700 dark:text-gray-300 break-all">
          {location.pathname}
        </code>
        . It may have been renamed, or the link that sent you here may be out of date.
      </p>

      <ul className="mt-6 space-y-2">
        {WAYS_OUT.map((way) => (
          <li key={way.path}>
            <Link
              to={way.path}
              className="flex items-baseline gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {way.label}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{way.blurb}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
